import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { DataTable, Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui';
import type { Column } from '@/components/ui';
import type { AuditLogEntry, AuditAction, AuditCategory, Restaurant } from '@/types/api';
import { csvRow, downloadCsv } from '@/utils/reportExport';
import {
  Search,
  Filter,
  Download,
  Eye,
  RefreshCw,
  User,
  Database,
  Key,
  Settings,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const categoryIcons: Record<AuditCategory, typeof User> = {
  AUTH: Key,
  DATA: Database,
  ACCESS: User,
  SYSTEM: Settings,
  SENSITIVE: AlertTriangle,
};

const actionColors: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  LOGIN_SUCCESS: 'bg-green-100 text-green-800',
  LOGIN_FAILED: 'bg-red-100 text-red-800',
  LOGOUT: 'bg-gray-100 text-gray-800',
};

export default function AuditLogPage() {
  const scope = useAuthStore((s) => s.scope);
  const canReadGroups = useHasPermission(PERM.GROUP_READ);
  const canReadRestaurants = useHasPermission(PERM.RESTAURANT_READ);
  // GET /v1/audit/verify is gated to system.admin only on the backend --
  // mirror that here rather than showing an action that would just 403 (C1).
  const canVerifyIntegrity = useHasPermission(PERM.SYSTEM_ADMIN);

  const isPlatformOrMixed = scope?.type === 'PLATFORM' || scope?.type === 'MIXED';
  const showGroupFilter = canReadGroups || isPlatformOrMixed;
  const showRestaurantFilter = canReadRestaurants || scope?.type === 'GROUP' || isPlatformOrMixed;

  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [filters, setFilters] = useState<{
    category?: AuditCategory;
    action?: AuditAction;
    entityType?: string;
    startDate?: string;
    endDate?: string;
    groupId?: number;
    restaurantId?: number;
  }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [auditPage, setAuditPage] = useState(0);
  const AUDIT_PAGE_SIZE = 50;

  // A filter change can leave auditPage pointing past the new result set's
  // last page (or just showing an unrelated page of a different scope) --
  // reset to the first page whenever the filter set changes (H2).
  useEffect(() => {
    setAuditPage(0);
  }, [filters]);

  // Load groups if the user can see the group filter
  const { data: groups } = useQuery({
    queryKey: ['groups'],
    queryFn: () => apiClient.getGroups(),
    enabled: showGroupFilter,
    staleTime: 5 * 60 * 1000,
  });

  // Load all restaurants if the user can see the restaurant filter. Paginated
  // with no picker-friendly "give me everything" option, so walk every page
  // instead of hardcoding a page size that would silently truncate the
  // dropdown past that many restaurants (M1).
  const { data: allRestaurants = [] } = useQuery<Restaurant[]>({
    queryKey: ['restaurants-all'],
    queryFn: async () => {
      const all: Restaurant[] = [];
      let page = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await apiClient.getRestaurants(page, 200);
        all.push(...result.content);
        if (result.last) break;
        page += 1;
      }
      return all;
    },
    enabled: showRestaurantFilter,
    staleTime: 5 * 60 * 1000,
  });

  // Filter the restaurant list by the selected group
  const visibleRestaurants = useMemo(() => {
    if (filters.groupId != null) {
      return allRestaurants.filter((r) => r.groupId === filters.groupId);
    }
    return allRestaurants;
  }, [allRestaurants, filters.groupId]);

  const { data: auditData, isLoading, isError, refetch } = useQuery({
    queryKey: ['auditLogs', filters, auditPage],
    queryFn: () =>
      apiClient.getAuditLogs({
        category: filters.category,
        action: filters.action,
        entityType: filters.entityType,
        startDate: filters.startDate,
        endDate: filters.endDate,
        restaurantId: filters.restaurantId,
        groupId: filters.groupId,
        page: auditPage,
        size: AUDIT_PAGE_SIZE,
      }),
  });

  function handleGroupChange(groupId: number | undefined) {
    // Clear restaurant selection when group changes
    setFilters((prev) => ({ ...prev, groupId, restaurantId: undefined }));
  }

  const columns: Column<AuditLogEntry>[] = [
    {
      key: 'timestamp',
      header: 'Time',
      sortable: true,
      render: (entry) => (
        <span className="text-sm text-gray-900">
          {new Date(entry.timestamp).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'userEmail',
      header: 'User',
      render: (entry) => (
        <div className="text-sm">
          <span className="text-gray-900">{entry.userEmail || 'SYSTEM'}</span>
          {entry.userRole && <span className="text-gray-500 block text-xs">{entry.userRole}</span>}
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (entry) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            actionColors[entry.action] || 'bg-gray-100 text-gray-800'
          }`}
        >
          {entry.action.replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (entry) => {
        const Icon = categoryIcons[entry.category] || Settings;
        return (
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <Icon className="h-4 w-4" />
            <span>{entry.category}</span>
          </div>
        );
      },
    },
    {
      key: 'entityType',
      header: 'Entity',
      render: (entry) =>
        entry.entityType ? (
          <div className="text-sm">
            <span className="font-medium text-gray-900">{entry.entityName || entry.entityId}</span>
            <span className="text-gray-500 block text-xs">{entry.entityType}</span>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: 'ipAddress',
      header: 'IP Address',
      render: (entry) => (
        <span className="font-mono text-xs text-gray-600">{entry.ipAddress || '-'}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (entry) => (
        <button
          onClick={() => setSelectedEntry(entry)}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="View details"
        >
          <Eye className="h-4 w-4" />
        </button>
      ),
    },
  ];

  const filteredData = auditData?.content?.filter((entry) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      entry.userEmail?.toLowerCase().includes(query) ||
      entry.entityName?.toLowerCase().includes(query) ||
      entry.entityType?.toLowerCase().includes(query) ||
      entry.entityId?.toLowerCase().includes(query)
    );
  });

  // Exports exactly what's currently on screen (post-filter, post-search) --
  // not the full server-side result set, which the "Showing N entries" count
  // right below the button already makes clear (H4).
  const handleExport = () => {
    const rows = filteredData ?? [];
    const header = [
      'Event ID', 'Timestamp', 'User Email', 'User Role', 'Action', 'Category',
      'Entity Type', 'Entity ID', 'Entity Name', 'Restaurant ID', 'IP Address', 'Record Hash',
    ];
    const lines = [csvRow(header)];
    rows.forEach((entry) => {
      lines.push(csvRow([
        entry.eventId,
        entry.timestamp,
        entry.userEmail ?? 'SYSTEM',
        entry.userRole ?? '',
        entry.action,
        entry.category,
        entry.entityType ?? '',
        entry.entityId ?? '',
        entry.entityName ?? '',
        entry.restaurantId ?? '',
        entry.ipAddress ?? '',
        entry.recordHash,
      ]));
    });
    downloadCsv(`audit-log-${new Date().toISOString().slice(0, 10)}.csv`, lines.join('\r\n'));
  };

  // Runs the real hash-chain verification (AuditLogService.verifyIntegrity)
  // over the current date filter, defaulting to the last 30 days when no
  // range is set -- replacing the per-entry chainValid indicator that was
  // hardcoded true server-side and never actually checked anything (C1).
  const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);
  const verifyMutation = useMutation({
    mutationFn: (range: { start: string; end: string }) =>
      apiClient.verifyAuditIntegrity(range.start, range.end),
  });
  const verifyRange = {
    start: filters.startDate || toIsoDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
    end: filters.endDate || toIsoDate(new Date()),
  };
  const handleVerifyIntegrity = () => verifyMutation.mutate(verifyRange);
  const handleViewBrokenEntry = async (eventId: string) => {
    try {
      setSelectedEntry(await apiClient.getAuditEntry(eventId));
    } catch {
      // Entry may no longer be visible under the current scope/filters;
      // the eventId is still shown in the verification result text below.
    }
  };

  const activeFilterCount = [
    filters.groupId,
    filters.restaurantId,
    filters.category,
    filters.action,
    filters.entityType,
    filters.startDate,
    filters.endDate,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="mt-1 text-sm text-gray-500">
            Immutable audit trail of all system actions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="btn btn-outline h-10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={!filteredData?.length}
            className="btn btn-outline h-10"
            title="Export the currently displayed entries as CSV"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          {canVerifyIntegrity && (
            <button
              onClick={handleVerifyIntegrity}
              disabled={verifyMutation.isPending}
              className="btn btn-outline h-10"
              title={`Runs a real hash-chain check for ${verifyRange.start} to ${verifyRange.end}`}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {verifyMutation.isPending ? 'Verifying…' : 'Verify Integrity'}
            </button>
          )}
        </div>
      </div>

      {(verifyMutation.data || verifyMutation.isError) && (
        <div
          className={`card p-4 flex items-start justify-between gap-4 ${
            verifyMutation.isError || verifyMutation.data?.valid === false
              ? 'bg-red-50 border-red-200'
              : 'bg-green-50 border-green-200'
          }`}
        >
          <div className="flex items-start gap-2">
            {verifyMutation.isError || verifyMutation.data?.valid === false ? (
              <XCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
            )}
            <div className="text-sm">
              <p className={verifyMutation.isError || verifyMutation.data?.valid === false ? 'text-red-800' : 'text-green-800'}>
                {verifyMutation.isError
                  ? (verifyMutation.error as any)?.response?.data?.message || 'Verification failed'
                  : verifyMutation.data?.message}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Range checked: {verifyRange.start} to {verifyRange.end}
              </p>
              {verifyMutation.data?.brokenAt && (
                <button
                  onClick={() => handleViewBrokenEntry(verifyMutation.data!.brokenAt!)}
                  className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                >
                  View entry {verifyMutation.data.brokenAt.slice(0, 8)}...
                </button>
              )}
            </div>
          </div>
          <button
            onClick={() => verifyMutation.reset()}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by user, entity, or ID..."
              className="input w-full pl-10"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn btn-outline h-10 relative ${showFilters ? 'bg-gray-100' : ''}`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-medium">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
            {/* Scope filters row — group + restaurant */}
            {(showGroupFilter || showRestaurantFilter) && (
              <div className="grid grid-cols-2 gap-4">
                {showGroupFilter && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                    <select
                      value={filters.groupId ?? ''}
                      onChange={(e) =>
                        handleGroupChange(e.target.value ? Number(e.target.value) : undefined)
                      }
                      className="input w-full"
                    >
                      <option value="">All Groups</option>
                      {(groups ?? []).map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {showRestaurantFilter && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Restaurant
                    </label>
                    <select
                      value={filters.restaurantId ?? ''}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          restaurantId: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      className="input w-full"
                    >
                      <option value="">
                        {filters.groupId != null ? 'All in group' : 'All Restaurants'}
                      </option>
                      {visibleRestaurants.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Standard filters row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={filters.category || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      category: (e.target.value || undefined) as AuditCategory,
                    })
                  }
                  className="input w-full"
                >
                  <option value="">All Categories</option>
                  <option value="AUTH">Authentication</option>
                  <option value="DATA">Data Mutations</option>
                  <option value="ACCESS">Access Control</option>
                  <option value="SYSTEM">System Events</option>
                  <option value="SENSITIVE">Sensitive Ops</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <select
                  value={filters.action || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      action: (e.target.value || undefined) as AuditAction,
                    })
                  }
                  className="input w-full"
                >
                  <option value="">All Actions</option>
                  <option value="CREATE">Create</option>
                  <option value="UPDATE">Update</option>
                  <option value="DELETE">Delete</option>
                  <option value="LOGIN_SUCCESS">Login Success</option>
                  <option value="LOGIN_FAILED">Login Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                <input
                  type="text"
                  value={filters.entityType || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, entityType: e.target.value || undefined })
                  }
                  placeholder="e.g., MenuItem"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value || undefined })
                  }
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value || undefined })
                  }
                  className="input w-full"
                />
              </div>
            </div>

            {activeFilterCount > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => setFilters({})}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 text-sm text-gray-600">
          {searchQuery
            ? `${filteredData?.length ?? 0} of ${auditData?.content?.length ?? 0} entries on this page match "${searchQuery}"`
            : `Showing ${auditData?.content?.length ?? 0} of ${auditData?.totalElements ?? 0} entries (page ${auditPage + 1} of ${Math.max(auditData?.totalPages ?? 1, 1)})`}
          {filters.groupId != null && groups && (
            <span className="ml-2 text-blue-600">
              · Group: {groups.find((g) => g.id === filters.groupId)?.name}
            </span>
          )}
          {filters.restaurantId != null && (
            <span className="ml-2 text-blue-600">
              · Restaurant:{' '}
              {allRestaurants.find((r) => r.id === filters.restaurantId)?.name ??
                `#${filters.restaurantId}`}
            </span>
          )}
          {searchQuery && (
            <span className="block text-xs text-gray-400 mt-0.5">
              Search only matches entries already loaded on this page -- use the filters above to narrow a larger result set.
            </span>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
            <p className="text-gray-900 font-medium mb-3">Failed to load audit entries</p>
            <button onClick={() => refetch()} className="btn btn-outline h-10">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        ) : (
          <DataTable<AuditLogEntry>
            data={filteredData || []}
            columns={columns}
            keyExtractor={(entry) => entry.eventId}
            showPagination={false}
            emptyMessage="No audit entries found"
          />
        )}
      </div>

      {/* Server-side pagination -- each fetch is already exactly one page's
          worth of results, unlike the old hardcoded page:0,size:100 that made
          anything past the 100th most recent matching entry unreachable (H2). */}
      {!isLoading && !isError && (auditData?.totalPages ?? 0) > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {auditPage + 1} of {auditData?.totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAuditPage((p) => Math.max(0, p - 1))}
              disabled={auditPage === 0}
              className="btn btn-outline h-9 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setAuditPage((p) => Math.min((auditData?.totalPages ?? 1) - 1, p + 1))}
              disabled={auditPage >= (auditData?.totalPages ?? 1) - 1}
              className="btn btn-outline h-9 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedEntry && (
        <AuditDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
      )}
    </div>
  );
}

interface AuditDetailModalProps {
  entry: AuditLogEntry;
  onClose: () => void;
}

function AuditDetailModal({ entry, onClose }: AuditDetailModalProps) {
  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>
        Audit Entry: {entry.eventId.slice(0, 8)}...
      </ModalHeader>
      <ModalBody>
        <div className="space-y-6">
          {/* Header Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Action:</span>
              <span
                className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  actionColors[entry.action] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {entry.action.replace(/_/g, ' ')}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Category:</span>
              <span className="ml-2 text-gray-900">{entry.category}</span>
            </div>
            <div>
              <span className="text-gray-500">Time:</span>
              <span className="ml-2 text-gray-900">
                {new Date(entry.timestamp).toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Entity:</span>
              <span className="ml-2 text-gray-900">
                {entry.entityType ? `${entry.entityType} #${entry.entityId}` : 'N/A'}
              </span>
            </div>
            {entry.restaurantId && (
              <div>
                <span className="text-gray-500">Restaurant ID:</span>
                <span className="ml-2 text-gray-900">#{entry.restaurantId}</span>
              </div>
            )}
          </div>

          {/* Who Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">WHO</h4>
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="text-gray-500 w-24">User:</span>
                <span className="text-gray-900">{entry.userEmail || 'SYSTEM'}</span>
              </div>
              {entry.userRole && (
                <div className="flex">
                  <span className="text-gray-500 w-24">Role:</span>
                  <span className="text-gray-900">{entry.userRole}</span>
                </div>
              )}
              {entry.ipAddress && (
                <div className="flex">
                  <span className="text-gray-500 w-24">IP:</span>
                  <span className="text-gray-900 font-mono">{entry.ipAddress}</span>
                </div>
              )}
              {entry.impersonatorId && (
                <div className="flex">
                  <span className="text-gray-500 w-24">Impersonator:</span>
                  <span className="text-orange-600">User #{entry.impersonatorId}</span>
                </div>
              )}
            </div>
          </div>

          {/* Changes Section */}
          {(entry.beforeValue || entry.afterValue) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">CHANGES</h4>
              <div className="grid grid-cols-2 gap-4">
                {entry.beforeValue && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Before</span>
                    <pre className="mt-1 bg-white border border-gray-200 rounded p-2 text-xs font-mono overflow-auto max-h-32">
                      {JSON.stringify(entry.beforeValue, null, 2)}
                    </pre>
                  </div>
                )}
                {entry.afterValue && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">After</span>
                    <pre className="mt-1 bg-white border border-gray-200 rounded p-2 text-xs font-mono overflow-auto max-h-32">
                      {JSON.stringify(entry.afterValue, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata Section */}
          {entry.metadata && Object.keys(entry.metadata).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">METADATA</h4>
              <pre className="bg-white border border-gray-200 rounded p-2 text-xs font-mono overflow-auto max-h-32">
                {JSON.stringify(entry.metadata, null, 2)}
              </pre>
            </div>
          )}

          {/* Integrity Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">INTEGRITY</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className="text-gray-500 w-28">Record Hash:</span>
                <span className="text-gray-900 font-mono text-xs">
                  {entry.recordHash ? `${entry.recordHash.slice(0, 32)}...` : '—'}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                This record's own hash-chain link is real, but a single entry can't be
                checked in isolation -- use "Verify Integrity" on the audit log page to
                run a real range check that includes this entry's date.
              </p>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button onClick={onClose} className="btn btn-outline h-10">
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
}
